import React, { useRef, useState, useContext, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MainCard from 'ui-component/cards/MainCard';
import useAppSettings from 'hooks/useAppSettings';
import Transitions from 'ui-component/extended/Transitions';
import JWTContext from 'contexts/JWTContext';
import axiosServices from 'utils/axios';
import { IconChevronDown, IconLogout, IconUser, IconFileText, IconShieldCheck } from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';
import { formatBrlCurrency } from 'utils/userFormatters';

// Iniciais do nome (até 2 letras) para o avatar
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

export default function ProfileSection() {
  const appSettings = useAppSettings();
  const theme = useTheme();
  const {
    state: { borderRadius }
  } = useConfig();
  const { user, logout, updateUser } = useContext(JWTContext);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [displayUser, setDisplayUser] = useState(user);
  const [userBalance, setUserBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    setDisplayUser(user);
  }, [user]);

  useEffect(() => {
    if (!open || !displayUser?.id) {
      return;
    }

    let mounted = true;
    (async () => {
      setBalanceLoading(true);
      try {
        const { data } = await axiosServices.get(`/api/v1/users/${displayUser.id}/balance`);
        if (mounted) setUserBalance(data.balance);
      } catch (error) {
        console.error('[balance fetch]', error?.response?.data || error);
        if (mounted) setUserBalance(null);
      } finally {
        if (mounted) setBalanceLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, displayUser?.id]);

  const handleLogout = async () => {
    await logout();
  };

  const handleListItemClick = (event, index, route = '') => {
    setSelectedIndex(index);
    handleClose(event);
    if (route) window.location.href = route;
  };

  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) {
        refreshUserData();
      }
      return !prevOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const refreshUserData = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      const { data } = await axiosServices.get('/api/v1/users/current');
      if (updateUser) {
        updateUser(data);
      }
    } catch (error) {
      console.error('Failed to refresh user', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const userName = displayUser?.full_name || '—';
  const userSubtitle = displayUser?.current_membership?.role_pt || displayUser?.current_membership?.role || '—';

  // Cores do cabeçalho (Personalização) ou as do tema
  const pillBg = appSettings?.navbar_icon_bg_color || theme.palette.primary.light;
  const pillFg = appSettings?.navbar_font_color || theme.palette.primary.main;

  const item = (index, label, icon, onClick) => (
    <ListItemButton sx={{ borderRadius: `${borderRadius}px`, py: 1 }} selected={selectedIndex === index} onClick={onClick}>
      <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
      <ListItemText primary={<Typography variant="body2">{label}</Typography>} />
    </ListItemButton>
  );

  return (
    <>
      {/* Botão do usuário: avatar com as iniciais + seta (antes era um Chip com a engrenagem, desalinhado) */}
      <ButtonBase
        ref={anchorRef}
        onClick={handleToggle}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        aria-label="Menu do usuário"
        title={userName}
        sx={{
          ml: 1.5,
          height: 40,
          pl: 0.5,
          pr: 1,
          gap: 0.5,
          borderRadius: '20px',
          color: pillFg,
          bgcolor: pillBg,
          transition: 'all .2s ease-in-out',
          '&:hover': { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText },
          '&:hover .gix-avatar': { bgcolor: theme.palette.primary.contrastText, color: theme.palette.primary.main }
        }}
      >
        <Avatar
          className="gix-avatar"
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.8rem',
            fontWeight: 700,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            transition: 'all .2s ease-in-out'
          }}
        >
          {initials(displayUser?.full_name)}
        </Avatar>
        <IconChevronDown
          stroke={2}
          size="16px"
          style={{ transition: 'transform .2s ease-in-out', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                elevation={16}
                sx={{
                  borderRadius: 3,
                  minWidth: 300,
                  overflow: 'hidden',
                  ...(appSettings?.user_menu_color
                    ? {
                        background: `linear-gradient(135deg, ${appSettings.user_menu_color}, ${appSettings.user_menu_color_back || appSettings.user_menu_color})`
                      }
                    : {}),
                  ...(appSettings?.user_menu_font_color
                    ? {
                        color: appSettings.user_menu_font_color,
                        '& .MuiTypography-root, & .MuiListItemText-root, & .MuiSvgIcon-root, & .MuiListItemIcon-root, & .MuiListItemIcon-root svg':
                          {
                            color: appSettings.user_menu_font_color
                          }
                      }
                    : {})
                }}
              >
                <MainCard
                  border={false}
                  elevation={0}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[0]}
                  sx={{
                    background: 'transparent',
                    ...(appSettings?.user_menu_color
                      ? {
                          background: `linear-gradient(135deg, ${appSettings.user_menu_color}, ${appSettings.user_menu_color_back || appSettings.user_menu_color})`
                        }
                      : {})
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        fontWeight: 700,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText
                      }}
                    >
                      {initials(displayUser?.full_name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h5" fontWeight={700} noWrap>
                        {userName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {userSubtitle}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Saldo: ${
                        balanceLoading ? 'carregando…' : userBalance !== null ? `R$ ${formatBrlCurrency(userBalance)}` : '-'
                      }`}
                    />
                  </Box>

                  <Divider />

                  <List component="nav" sx={{ width: '100%', minWidth: 280, px: 1, py: 0.5, '& .MuiListItemButton-root': { mt: 0.25 } }}>
                    {item(98, 'Editar', <IconUser stroke={1.5} size="20px" />, (e) => handleListItemClick(e, 98, '/user/edit'))}
                    {item(99, 'Log', <IconFileText stroke={1.5} size="20px" />, (e) => handleListItemClick(e, 99, '/user/log'))}
                    {item(100, 'Privacidade (LGPD)', <IconShieldCheck stroke={1.5} size="20px" />, (e) =>
                      handleListItemClick(e, 100, '/privacidade')
                    )}
                  </List>

                  <Divider />

                  <List component="nav" sx={{ px: 1, py: 0.5 }}>
                    {item(101, 'Sair', <IconLogout stroke={1.5} size="20px" />, handleLogout)}
                  </List>
                </MainCard>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
